export const isRuntimeActive = (segment) => segment.started !== null && segment.ended === null;
export const findSnapshotActiveSegment = (eventSnapshot) => eventSnapshot?.segments.find((segment) => segment.isActive) ?? null;
export const findActiveSegmentId = (eventSnapshot) => findSnapshotActiveSegment(eventSnapshot)?.id ?? null;
export const isSegmentStalled = (segment) => segment !== null && segment.status === 'recording' && !isRuntimeActive(segment);
export const canStartRecordingForEvent = (eventSnapshot) => findSnapshotActiveSegment(eventSnapshot) === null;
export const findSegment = (eventSnapshot, segmentId) => {
    if (segmentId === null || eventSnapshot === null) {
        return null;
    }
    return eventSnapshot.segments.find((s) => s.id === segmentId) ?? null;
};
export const findActiveRecordingSegment = (eventSnapshot) => eventSnapshot?.segments.find((s) => s.status === 'recording') ?? null;
export const findProcessedAudioSeconds = (telemetry, segmentId) => {
    if (segmentId === null || telemetry === null) {
        return null;
    }
    const segment = telemetry.segments.find((s) => s.segmentId === segmentId);
    return segment?.processedAudioSeconds ?? null;
};
export const AUDIO_GAP_THRESHOLD_SEC = 5;
export const isSegmentActivelyRecording = (eventSnapshot, segmentId) => {
    const segment = findSegment(eventSnapshot, segmentId);
    return segment !== null && isRuntimeActive(segment);
};
/** Gap in seconds since last upload; uses server `sampledAtUtc` by default (refresh-safe). */
export const secondsSinceLastUpload = (telemetry, segmentId, nowMs) => {
    if (segmentId === null || telemetry === null) {
        return null;
    }
    const segment = telemetry.segments.find((s) => s.segmentId === segmentId);
    if (segment === undefined || segment.lastUploadActivityUtc === null) {
        return null;
    }
    const referenceMs = nowMs ?? Date.parse(telemetry.sampledAtUtc);
    const lastMs = Date.parse(segment.lastUploadActivityUtc);
    if (Number.isNaN(referenceMs) || Number.isNaN(lastMs)) {
        return null;
    }
    return Math.max(0, Math.floor((referenceMs - lastMs) / 1000));
};
export const getAudioConfigStatus = (eventSnapshot) => {
    if (eventSnapshot === null) {
        return null;
    }
    const connectionsWithSources = eventSnapshot.connections.filter((connection) => connection.audioSources.length > 0);
    if (connectionsWithSources.length === 0) {
        return 'noSources';
    }
    const selectedConnectionId = eventSnapshot.selectedAudioSourceConnectionId;
    if (selectedConnectionId === null) {
        return 'noSourceSelected';
    }
    const selectedConnection = eventSnapshot.connections.find((connection) => connection.id === selectedConnectionId);
    if (selectedConnection === undefined
        || selectedConnection.selectedAudioSourceDeviceId === null
        || selectedConnection.selectedAudioSourceDeviceId === '') {
        return 'noMicrophone';
    }
    if (eventSnapshot.audioMuted) {
        return 'audioMuted';
    }
    return null;
};
export const isSegmentCompleted = (segment) => segment?.status === 'completed';
export const isSegmentTerminating = (segment) => segment !== null
    && segment.status !== 'completed'
    && segment.stopReason != null;
export const findRuntimeActiveSegment = (eventSnapshot) => eventSnapshot?.segments.find(isRuntimeActive) ?? null;
/** Returns new selected id, or undefined when selection should stay unchanged. */
export const pickSelectedSegmentIdOnActiveChange = (previousActiveSegmentId, activeSegmentId, selectedSegmentId) => {
    if (activeSegmentId === previousActiveSegmentId) {
        return undefined;
    }
    if (activeSegmentId !== null) {
        return activeSegmentId;
    }
    if (selectedSegmentId === previousActiveSegmentId) {
        return null;
    }
    return undefined;
};
export const getMicAudioStatus = ({ eventSnapshot, segmentId, }) => {
    const segment = findSegment(eventSnapshot, segmentId);
    if (isSegmentCompleted(segment)) {
        return { kind: 'ready' };
    }
    const audioConfigStatus = getAudioConfigStatus(eventSnapshot);
    if (audioConfigStatus !== null) {
        return { kind: 'audioConfig', reason: audioConfigStatus };
    }
    return { kind: 'ready' };
};
export const getRecordingStatusDisplay = ({ connectionHealth, connectionReady, degradedSeconds, eventSnapshot, telemetry, segmentId, nowMs, }) => {
    const isRecording = isSegmentActivelyRecording(eventSnapshot, segmentId);
    const showTransportStatus = connectionReady || isRecording;
    if (showTransportStatus && connectionHealth === 'offline') {
        return { kind: 'offline', seconds: degradedSeconds };
    }
    if (showTransportStatus && connectionHealth === 'networkIssue') {
        return { kind: 'networkIssue' };
    }
    const segment = findSegment(eventSnapshot, segmentId);
    if (isSegmentStalled(segment)) {
        return { kind: 'stalled' };
    }
    if (!isSegmentCompleted(segment)) {
        const audioConfigStatus = getAudioConfigStatus(eventSnapshot);
        if (audioConfigStatus !== null) {
            return { kind: 'audioConfig', reason: audioConfigStatus };
        }
    }
    if (!isRecording) {
        return null;
    }
    const telemetrySegment = segmentId === null || telemetry === null
        ? null
        : telemetry.segments.find((s) => s.segmentId === segmentId);
    if (segment !== null && isRuntimeActive(segment) && telemetrySegment !== undefined) {
        const gap = secondsSinceLastUpload(telemetry, segmentId, nowMs);
        if (gap !== null && gap >= AUDIO_GAP_THRESHOLD_SEC) {
            return { kind: 'noAudio', seconds: gap };
        }
    }
    return { kind: 'running' };
};
export const pickInitialSelectedSegmentId = (eventSnapshot) => {
    if (eventSnapshot === null) {
        return null;
    }
    const runtimeActive = findSnapshotActiveSegment(eventSnapshot);
    if (runtimeActive !== null) {
        return runtimeActive.id;
    }
    const incomplete = eventSnapshot.segments.filter((s) => s.status !== 'completed');
    if (incomplete.length === 0) {
        return null;
    }
    const staleRecording = [...incomplete].reverse().find((s) => s.status === 'recording');
    if (staleRecording !== undefined) {
        return staleRecording.id;
    }
    return incomplete[incomplete.length - 1].id;
};
/** Cycle: seg[0] → … → seg[n-1] → null (new) → seg[0]. Missing current id → null. */
export const pickAdjacentSegmentIdInOrder = (eventSnapshot, currentSegmentId, direction) => {
    const segments = eventSnapshot?.segments;
    if (segments === undefined || segments.length === 0) {
        return null;
    }
    if (currentSegmentId === null) {
        return direction === 'next'
            ? segments[0].id
            : segments[segments.length - 1].id;
    }
    const currentIndex = segments.findIndex((s) => s.id === currentSegmentId);
    if (currentIndex < 0) {
        return null;
    }
    if (direction === 'next') {
        return currentIndex >= segments.length - 1
            ? null
            : segments[currentIndex + 1].id;
    }
    return currentIndex <= 0
        ? null
        : segments[currentIndex - 1].id;
};
export const pickNextSegmentIdInOrder = (eventSnapshot, currentSegmentId) => pickAdjacentSegmentIdInOrder(eventSnapshot, currentSegmentId, 'next');
export const pickPreviousSegmentIdInOrder = (eventSnapshot, currentSegmentId) => pickAdjacentSegmentIdInOrder(eventSnapshot, currentSegmentId, 'previous');
export const getRecordTabUiState = ({ eventSnapshot, selectedSegmentId, transportOnline, actionWaiting, }) => {
    const segment = findSegment(eventSnapshot, selectedSegmentId);
    const status = segment?.status;
    const isStalled = isSegmentStalled(segment);
    const isRuntimeActiveSegment = segment !== null && isRuntimeActive(segment);
    const isTerminating = isSegmentTerminating(segment);
    const formDisabled = actionWaiting
        || isTerminating
        || (selectedSegmentId !== null && segment === null)
        || (transportOnline && isRuntimeActiveSegment)
        || (transportOnline && status === 'completed')
        || (!transportOnline && status === 'completed');
    const segmentCanStart = selectedSegmentId === null
        || status === 'created';
    const showStart = transportOnline
        && !actionWaiting
        && canStartRecordingForEvent(eventSnapshot)
        && segmentCanStart
        && status !== 'recording'
        && status !== 'completed';
    const showFinish = transportOnline
        && !actionWaiting
        && !isTerminating
        && (status === 'recording' || isStalled || isRuntimeActiveSegment);
    const startDisabled = actionWaiting;
    const finishDisabled = actionWaiting;
    const showNextSegment = !actionWaiting
        && selectedSegmentId !== null
        && !showStart
        && !showFinish
        && (status === 'completed' || segment === null);
    return {
        formDisabled,
        showStart,
        showFinish,
        showNextSegment,
        startDisabled,
        finishDisabled,
    };
};
